import React, { Fragment, useEffect, useState, useContext, useRef, createRef } from "react";
import { useTranslation } from "react-i18next";

// MUI
import { Box, Typography, CircularProgress, Button, SvgIcon, Stack } from "@mui/material";
import StraightIcon from '@mui/icons-material/Straight';

import WeatherChart from "@/ui/Charts/WeatherChart"

// @ts-ignore
import api from "@/lib/api";

type WeatherData = {
  dayOfWeek: number | undefined;
  temperature: string | undefined;
  icon: string | undefined;
  wind_speed: number | undefined;
  wind_deg: number | undefined;
};

type HourlyWeatherData = {
  temperature: number | undefined;
  precipitationPercent: number | undefined;
  weatherDescription: string | undefined;
  hour: number;
  icon: string
}

function WeatherCard(props: {lat: number, lon: number, fetchData: boolean}) {
  const { t } = useTranslation();

  const chartConteinerRef = createRef<HTMLDivElement>();

  const {lat, lon, fetchData} = props;
  const [weatherData, setWeatherData] = useState<any>(undefined);
  const [weatherDataParsed, setWeatherDataParsed] = useState<Array<WeatherData> >([]);
  const [weatherDataHourly, setWeatherDataHourly] = useState<Array<HourlyWeatherData> >([]);
  const [measureDate, setMeasureDate] = useState<Date>(new Date());

  useEffect(()=>{ //USED FOR FETCH VIA MW
    if (fetchData===false) return;

    setWeatherData(undefined);
    setWeatherDataParsed([]);
    setWeatherDataHourly([]);
    
    const apiInstance = new api();

    const path=`maps/weatherData?lat=${lat}&lon=${lon}`
    apiInstance.Call(path, "get")
    .then((response)=> {
      if(response.success){
        setWeatherData(response.data);
      } else {
        setWeatherData(null);
      }
    })
    .catch(() => {
      setWeatherData(null);
    })
  },[fetchData])
  
  useEffect(()=>{

    if (weatherData===undefined || weatherData===null) return;

    let tempWeatherDataParsed: Array<WeatherData> = [];
    let tempHourlyDataParsed: Array<HourlyWeatherData> = [];

    for (var i =0; i<5;i++)
    {

        var date = new Date(0);

        var daily;

        if(weatherData.data===undefined)
        {
          daily = weatherData.daily;
        }
        else
        {
          daily = weatherData.data.daily;
        }

        date.setUTCSeconds(daily[i]?.dt)

        const dayOfWeek = date.getDay();
        const temperature = Math.round(daily[i].temp.min) + "/" + Math.round(daily[i].temp.max)+"°" as string
        const icon = daily[i].weather[0].icon as string;
        const wind_deg = daily[i].wind_deg as number;
        const wind_speed = parseFloat((daily[i].wind_speed as number).toFixed(1));

        tempWeatherDataParsed[i] = {temperature,icon,dayOfWeek,wind_speed,wind_deg};

        if (tempWeatherDataParsed.length!==0)
        {
          setWeatherDataParsed([...tempWeatherDataParsed]);
        }
    }

    for(let i=0; i<48;i++) {
      var date = new Date(0);

      var hourly;

      if(weatherData.data===undefined)
      {
        hourly = weatherData.hourly;
      }
      else
      {
        hourly = weatherData.data.hourly;
      }

      const weatherDescription = hourly[i].weather[0].description as string;
      const temperature = Math.round(hourly[i].temp) as number; // + "°" .toFixed(1)?
      const precipitationPercent = Math.round(hourly[i].pop * 100) as number; // + "%"
      let unixTimestamp = hourly[i].dt;
      let dateObj = new Date(unixTimestamp * 1000);
      if(i === 0) {
        setMeasureDate(dateObj);
      }
      var hour = dateObj.getHours();
      const icon = hourly[i].weather[0].icon as string;

      tempHourlyDataParsed[i] = {weatherDescription, temperature, precipitationPercent, hour, icon};
    }

    if (tempHourlyDataParsed.length!==0) {
      setWeatherDataHourly([...tempHourlyDataParsed]);
    }

  },[weatherData])

  useEffect(() => {
    setWeatherDataParsed([]);
    setWeatherDataHourly([]);
  },
  [lat,lon]);

  useEffect(() => {

    const container = chartConteinerRef.current;

    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY !== 0 && container) {
        container.scrollLeft += event.deltaY;
        event.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [chartConteinerRef]); // Make sure to include all dependencies that are used inside the useEffect

  return (
    <>
      { 
        fetchData ? 
          weatherData !== null ?
            weatherDataParsed.length!==0 ? 
              <Box mb={2}>
                <Typography mx={2} variant="h6">{t("titles.weather_forecast")}</Typography>
                <Box alignSelf={"center"} justifySelf={"center"} display={"flex"} flexDirection={"row"} justifyContent={"space-between"} mt={1}mb={2}>
                {
                  weatherDataParsed?.map((weatherEntry,index) => {
                    return(
                      <Box p={0.5} key={index} width={"fit-content"} display={"flex"} flexDirection={"column"} alignItems={"center"}>
                        <Typography fontWeight={400} variant="body1">{t("common.day."+weatherEntry.dayOfWeek as string)}</Typography>
                        <img width={"64px"} height={"64px"} src={"images/icons/"+ weatherEntry.icon +".svg"}></img>
                        <Typography variant="body2">{weatherEntry.temperature}</Typography>
                        <Box mt={0.5} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                          <StraightIcon sx={{
                            transform: "rotate("+weatherEntry.wind_deg+"deg)"
                          }}/>
                          <Stack>
                            <Typography lineHeight={1.05} variant="body2">{weatherEntry.wind_speed}</Typography>
                            <Typography lineHeight={1.05} variant="body2">{"m/s"}</Typography>
                          </Stack>
                        </Box>

                      </Box>
                    )
                  })
                  }
                </Box>
                <Box ref={chartConteinerRef} id={"weather-chart"} sx={{
                      overflowX: "scroll",
                      overflowY: "hidden",
                    }}
                >
                  <WeatherChart 
                    title={t('public.weather.weather_48h')}
                    series={[{
                        name: t('public.weather.pop'),
                        type: 'column',
                        data: weatherDataHourly.map((h) => h.precipitationPercent)
                    }, {
                        name: t('public.weather.temperature'),
                        type: 'line',
                        data: weatherDataHourly.map((h) => h.temperature)
                    }]}
                    hourStep={3}
                    hours={weatherDataHourly.map((h) => h.hour)}
                    measureDate={measureDate}
                    icons={weatherDataHourly.map((h) => h.icon)}
                  />
                </Box>
              </Box>
            : 
              <Box width={"100%"} display={"flex"} justifyContent={"center"} mb={1}>
                <CircularProgress />
              </Box>  
          :
            <Box width={"100%"} display={"flex"} justifyContent={"center"} mb={1}>
              <Typography>{t('common.unavailable')}</Typography>
            </Box>
        :
          <></>
      }
    </>
  );
}

export default WeatherCard;
